'use client';

import type { Engine } from '@tsparticles/engine';

import { Box } from '@mui/material';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadImageShape } from '@tsparticles/shape-image';
import { loadTextShape } from '@tsparticles/shape-text';
import { loadSlim } from '@tsparticles/slim';
import { useState, useEffect } from 'react';

// Extended emoji and SVG map
export const emojiMap: Record<string, { type: 'emoji' | 'svg'; value: string }> = {
  // Original emoji set
  heart: { type: 'emoji', value: '❤️' },
  party: { type: 'emoji', value: '🎉' },
  sunglasses: { type: 'emoji', value: '😎' },
  clover: { type: 'emoji', value: '🍀' },
  pumpkin: { type: 'emoji', value: '🎃' },
  lollipop: { type: 'emoji', value: '🍭' },
  earth: { type: 'emoji', value: '🌍' },
  fire: { type: 'emoji', value: '🔥' },
  ghost: { type: 'emoji', value: '👻' },
  dragon: { type: 'emoji', value: '🐉' },
  cocktail: { type: 'emoji', value: '🍸' },
  gameday: { type: 'emoji', value: '🏀' },
  demon: { type: 'emoji', value: '👿' },
  alien: { type: 'emoji', value: '👽' },
  skull: { type: 'emoji', value: '💀' },
  canada: { type: 'emoji', value: '🇨🇦' },
  koala: { type: 'emoji', value: '🐨' },
  coffee: { type: 'emoji', value: '☕' },
  rainbow: { type: 'emoji', value: '🌈' },
  unicorn: { type: 'emoji', value: '🦄' },
  star: { type: 'emoji', value: '⭐' },
  moon: { type: 'emoji', value: '🌙' },
  sun: { type: 'emoji', value: '☀️' },
  pizza: { type: 'emoji', value: '🍕' },
  cake: { type: 'emoji', value: '🎂' },
  crown: { type: 'emoji', value: '👑' },
  guitar: { type: 'emoji', value: '🎸' },
  rose: { type: 'emoji', value: '🌹' },
  balloon: { type: 'emoji', value: '🎈' },
  rocket: { type: 'emoji', value: '🚀' },
  dolphin: { type: 'emoji', value: '🐬' },
  butterfly: { type: 'emoji', value: '🦋' },
  snowflake: { type: 'emoji', value: '❄️' },
  palm: { type: 'emoji', value: '🌴' },
  lightning: { type: 'emoji', value: '⚡' },
  music: { type: 'emoji', value: '🎵' },
  diamond: { type: 'emoji', value: '💎' },
  gift: { type: 'emoji', value: '🎁' },
  camera: { type: 'emoji', value: '📷' },
  book: { type: 'emoji', value: '📚' },
  lightbulb: { type: 'emoji', value: '💡' },
  penguin: { type: 'emoji', value: '🐧' },
  monkey: { type: 'emoji', value: '🐵' },
  car: { type: 'emoji', value: '🚗' },
  pinkBear: { type: 'svg', value: '/assets/svgs/pink_bear.svg' },
};

interface VibemojiThemeProps {
  variation?: string;
  color?: string;
  count?: number;
}

export function VibemojiTheme({
  variation = 'heart',
  color = '#000',
  count = 30,
}: VibemojiThemeProps) {
  const [init, setInit] = useState(false);

  console.log('VibemojiTheme initialized with:', {
    variation,
    color,
    count,
  });

  // Get the shape data
  const shapeData = emojiMap[variation] || { type: 'emoji', value: '❤️' };

  // Initialize tsParticles with both text and image shape plugins
  useEffect(() => {
    const initializeParticles = async () => {
      await initParticlesEngine(async (engine: Engine) => {
        // Load the slim bundle
        await loadSlim(engine);

        // Load both text and image shape plugins
        await loadTextShape(engine);
        await loadImageShape(engine);
      });

      setInit(true);
    };

    initializeParticles();
  }, []);

  if (!init) return null;

  // Create particle options based on shape type
  const getParticleOptions = () => {
    const baseOptions = {
      fullScreen: {
        enable: true,
        zIndex: 0,
      },
      background: {
        color: {
          value: color,
        },
      },
      fpsLimit: 60,
      particles: {
        number: {
          value: count,
          density: {
            enable: true,
            area: 800,
          },
        },
        size: {
          value: shapeData.type === 'svg' ? { min: 30, max: 70 } : { min: 20, max: 40 },
        },
        opacity: {
          value: 1,
        },
        move: {
          enable: true,
          speed: 2,
          direction: 'none',
          random: false,
          straight: false,
          outModes: {
            default: 'bounce',
          },
          attract: {
            enable: false,
            rotate: {
              x: 600,
              y: 1200,
            },
          },
        },
        rotate: {
          value: {
            min: 0,
            max: 360,
          },
          direction: 'random',
          animation: {
            enable: true,
            speed: 1,
          },
        },
      },
      detectRetina: true,
    };

    if (shapeData.type === 'emoji') {
      return {
        ...baseOptions,
        particles: {
          ...baseOptions.particles,
          shape: {
            type: 'character',
            options: {
              character: {
                value: shapeData.value,
                font: 'Verdana',
                style: '',
                weight: '400',
              },
            },
          },
        },
      };
    }
    return {
      ...baseOptions,
      particles: {
        ...baseOptions.particles,
        shape: {
          type: 'image',
          options: {
            image: {
              src: shapeData.value,
              width: 100,
              // Remove the fixed height to maintain aspect ratio
              // Or use replaceColor: false to preserve original colors
              replaceColor: false,
            },
          },
        },
      },
    };
  };

  const particlesOptions: any = getParticleOptions();

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
      }}
    >
      <Particles id="vibemoji-particles" options={particlesOptions} />
    </Box>
  );
}
